import { useState, useEffect } from 'react';
import { Loader2, GitBranch, Layers, Send, ListChecks, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTimestamp(isoString) {
  if (!isoString) return 'never';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  return then.toLocaleString('en-GB');
}

const TARGET_STATE_LABEL = {
  queued: 'queued',
  refused: 'refused (review-state excluded)',
  blocked_no_credential: 'blocked (no credential)',
};

function QueuePushNow({ invoiceId, sourceIsPennylane }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneQueuePushPreview(invoiceId);
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.pennylaneQueuePushApply(invoiceId);
      setApplied(data);
      if (data.applied) {
        toast.success(`Invoice ${data.invoice_number || invoiceId} queued for push`);
      } else {
        toast.error(data.error || 'Queue push failed');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!sourceIsPennylane) return null;

  return (
    <div className="border rounded p-2 bg-slate-50 dark:bg-slate-900/40 space-y-2">
      <div className="flex items-center gap-2">
        <Send className="h-3 w-3 text-muted-foreground" />
        <span className="font-medium">Queue push now</span>
        <Button
          data-testid={`queue-push-preview-${invoiceId}`}
          size="sm"
          variant="outline"
          onClick={runPreview}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Preview
        </Button>
        {preview?.found && (
          <Button
            data-testid={`queue-push-apply-${invoiceId}`}
            size="sm"
            onClick={runApply}
            disabled={loading}
          >
            Apply
          </Button>
        )}
      </div>

      {preview && !preview.found && (
        <div className="text-muted-foreground">invoice not found</div>
      )}
      {preview?.found && (
        <div className="pl-2 border-l-2 space-y-0.5" data-testid={`queue-push-preview-result-${invoiceId}`}>
          <div>entity_id={preview.entity_id ?? '-'}</div>
          <div>
            credential: {preview.credential_resolved ? `resolved (${preview.credential_source})` : 'not resolved'}
          </div>
          <div>current push_state: {preview.current_push_state ?? '(none)'}</div>
          <div>
            target push_state on Apply:{' '}
            <span className="font-medium">
              {preview.target_push_state ? (TARGET_STATE_LABEL[preview.target_push_state] || preview.target_push_state) : 'no effect'}
            </span>
          </div>
          <div className="text-muted-foreground">{preview.note}</div>
        </div>
      )}
      {applied && (
        <div className={cn('pl-2 border-l-2', applied.applied ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
          {applied.applied
            ? `Queued. push_queued_at=${fmtTimestamp(applied.push_queued_at)} -- picked up by the next retry run.`
            : applied.error}
        </div>
      )}
    </div>
  );
}

function StaleQueuedBanner() {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    diagnosticsAPI.pennylaneQueueHealth()
      .then(({ data }) => { if (!cancelled) setHealth(data); })
      .catch(() => { /* banner is best-effort; card body still works without it */ });
    return () => { cancelled = true; };
  }, []);

  if (!health || health.stale_count === 0) return null;

  return (
    <div
      className="flex items-start gap-2 rounded border border-red-300 bg-red-50 p-3 text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400"
      data-testid="pennylane-queue-stale-banner"
    >
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="text-xs">
        <div className="font-medium">
          {health.stale_count} invoice{health.stale_count === 1 ? '' : 's'} stuck at push_state=queued
          past the {health.threshold_hours}h watchdog threshold with zero attempts.
        </div>
        {health.oldest_invoice && (
          <div className="mt-0.5">
            Oldest: {health.oldest_invoice.invoice_number || health.oldest_invoice.id}
            {health.oldest_age_hours != null && ` -- queued ${health.oldest_age_hours.toFixed(1)}h ago`}.
            The retry job (every 6h) should be draining this -- if it hasn't, check whether
            scheduled_pennylane_reconciliation is actually firing.
          </div>
        )}
      </div>
    </div>
  );
}

export function PennylaneReconPropagationPanel() {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const q = invoiceNumber.trim();
    if (!q) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneReconPropagation(q);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Pennylane Recon Propagation</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. For one invoice: local match doc(s), its own accounting-sync fields,
          the stored (pulled) Pennylane reconciliation record(s), and any push-attempt
          sync_logs entries -- no live Pennylane calls, no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <StaleQueuedBanner />
        <div className="flex items-center gap-2">
          <Input
            data-testid="pennylane-recon-propagation-input"
            placeholder="Invoice number (F-2026-07-57) or invoice id"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            className="max-w-sm"
          />
          <Button data-testid="pennylane-recon-propagation-run" onClick={run} disabled={loading || !invoiceNumber.trim()} size="sm">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Run
          </Button>
        </div>

        {result && !result.found && (
          <p className="text-xs text-muted-foreground" data-testid="pennylane-recon-propagation-not-found">
            No invoice found for "{result.invoice_query}".
          </p>
        )}

        {result?.found && result.results.map((r, idx) => (
          <div key={r.invoice.id || idx} className="border rounded-lg p-3 space-y-3 text-xs" data-testid="pennylane-recon-propagation-result">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary">{r.invoice.invoice_number}</Badge>
              <span className="text-muted-foreground">id={r.invoice.id}</span>
              <span className="text-muted-foreground">source={r.invoice.source || '-'}</span>
            </div>

            <QueuePushNow invoiceId={r.invoice.id} sourceIsPennylane={r.invoice.source === 'pennylane'} />

            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <div className="col-span-2">
                <span className="text-muted-foreground">push_state:</span>{' '}
                {r.invoice.push_state ? (
                  <Badge variant="secondary" className={cn('text-xs',
                    r.invoice.push_state === 'synced' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                    r.invoice.push_state === 'queued' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                    r.invoice.push_state === 'syncing' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                    r.invoice.push_state === 'refused' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' :
                    r.invoice.push_state === 'blocked_no_credential' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                    r.invoice.push_state === 'attempted_failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                    'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                  )}>
                    {r.invoice.push_state}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">not set (pre-migration or non-Pennylane)</span>
                )}
                {r.invoice.push_state === 'queued' && (
                  <span className="text-muted-foreground ml-2">queued_at={fmtTimestamp(r.invoice.push_queued_at)}</span>
                )}
              </div>
              <div><span className="text-muted-foreground">matching_status:</span> {r.invoice.matching_status ?? '-'}</div>
              <div><span className="text-muted-foreground">review_status:</span> {r.invoice.review_status ?? '-'}</div>
              <div><span className="text-muted-foreground">payment_status:</span> {r.invoice.payment_status ?? '-'}</div>
              <div><span className="text-muted-foreground">accounting_synced:</span> {r.invoice.accounting_synced ?? '-'}</div>
              <div><span className="text-muted-foreground">accounting_synced_at:</span> {fmtTimestamp(r.invoice.accounting_synced_at)}</div>
              <div><span className="text-muted-foreground">pennylane_bank_matched:</span> {String(!!r.invoice.pennylane_bank_matched)}</div>
              {r.invoice.accounting_sync_error && (
                <div className="col-span-2 text-red-600 dark:text-red-400">
                  accounting_sync_error: {r.invoice.accounting_sync_error}
                </div>
              )}
            </div>

            <div>
              <div className="font-medium mb-1">Local match doc(s) ({r.local_matches.length})</div>
              {r.local_matches.length === 0 && <div className="text-muted-foreground">none</div>}
              {r.local_matches.map((m) => (
                <div key={m.id} className="pl-2 border-l-2 mb-1">
                  status={m.status}, transaction_id={m.transaction_id}, matched_amount={m.matched_amount},
                  {' '}created_at={fmtTimestamp(m.created_at)}, confirm_step={m.confirm_step ?? '-'}
                </div>
              ))}
            </div>

            <div>
              <div className="font-medium mb-1">Stored Pennylane reconciliation record(s) ({r.accounting_reconciliation_records.length})</div>
              {r.accounting_reconciliation_records.length === 0 && <div className="text-muted-foreground">none</div>}
              {r.accounting_reconciliation_records.map((rec, i) => (
                <div key={i} className="pl-2 border-l-2 mb-1">
                  transaction_id={rec.transaction_id}, mapping_status={rec.mapping_status}, reconciled={String(!!rec.reconciled)},
                  {' '}counterpart_pinned={String(!!rec.counterpart_pinned)}, pulled_at={fmtTimestamp(rec.pulled_at)}
                </div>
              ))}
            </div>

            <div>
              <div className="font-medium mb-1">Push-attempt log entries ({r.push_attempt_log_entries.length})</div>
              {r.push_attempt_log_entries.length === 0 && <div className="text-muted-foreground">none found naming this invoice</div>}
              {r.push_attempt_log_entries.map((log, i) => (
                <div key={i} className="pl-2 border-l-2 mb-1">
                  {log.action === 'push_attempt' ? (
                    // Per-invoice attempt entry (services/pennylane_push_retry.py)
                    <>
                      {fmtTimestamp(log.timestamp)} -- push_attempt ({log.scheduled ? 'scheduled' : 'manual'}) -- outcome=
                      <span className={cn(
                        log.outcome === 'synced' ? 'text-green-600 dark:text-green-400' :
                        log.outcome === 'refused' ? 'text-amber-600 dark:text-amber-400' :
                        log.outcome && log.outcome !== 'synced' ? 'text-red-600 dark:text-red-400' : ''
                      )}>{log.outcome}</span>
                      {log.error && <div className="text-red-600 dark:text-red-400">error: {log.error}</div>}
                    </>
                  ) : (
                    // Shared batch-run summary entry (mentions this invoice indirectly)
                    <>
                      {fmtTimestamp(log.timestamp)} -- {log.action} ({log.scheduled ? 'scheduled' : 'manual'})
                      {log.refused_entries?.length > 0 && (
                        <div className="text-amber-600 dark:text-amber-400">refused: {JSON.stringify(log.refused_entries)}</div>
                      )}
                      {log.error_entries?.length > 0 && (
                        <div className="text-red-600 dark:text-red-400">errors: {log.error_entries.join('; ')}</div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {r.diagnosis_notes.length > 0 && (
              <div className="border rounded p-2 bg-amber-50 dark:bg-amber-900/10 space-y-1">
                {r.diagnosis_notes.map((note, i) => (
                  <div key={i}>{note}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function PennylaneStuckInvoiceBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneQueueStuckPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.pennylaneQueueStuckApply();
      setApplied(data);
      if (data.applied) {
        toast.success(`${data.queued_count} stuck invoice(s) queued`);
      }
      // Re-preview so the card shows the population has drained, rather
      // than leaving a stale pre-apply count on screen.
      const { data: refreshed } = await diagnosticsAPI.pennylaneQueueStuckPreview();
      setPreview(refreshed);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Queue Stuck Invoices</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Bulk backfill for the historical Pennylane push backlog: invoices reconciled/paid
          locally (matched) but never (re-)queued -- push_state unset (pre-migration) or
          attempted_failed. Preview is read-only. Apply sets push_state="queued" on exactly the
          previewed population; it does not call Pennylane itself, only the retry job
          (scheduled every 6h, or Sync Reconciliation) does that. Idempotent -- re-Apply on an
          already-drained population queues 0.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button data-testid="queue-stuck-preview" size="sm" variant="outline" onClick={runPreview} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && preview.count > 0 && (
            <Button data-testid="queue-stuck-apply" size="sm" onClick={runApply} disabled={loading}>
              Apply
            </Button>
          )}
        </div>

        {preview && (
          <div className="text-xs space-y-2" data-testid="queue-stuck-preview-result">
            <div>{preview.note}</div>
            {preview.count > 0 && (
              <>
                <div className="flex gap-6">
                  <div>
                    <div className="font-medium mb-1">By entity</div>
                    {Object.entries(preview.by_entity).map(([entity, n]) => (
                      <div key={entity}>{entity}: {n}</div>
                    ))}
                  </div>
                  <div>
                    <div className="font-medium mb-1">By month</div>
                    {Object.entries(preview.by_month).map(([month, n]) => (
                      <div key={month}>{month}: {n}</div>
                    ))}
                  </div>
                </div>
                <table className="w-full text-xs border-collapse" data-testid="queue-stuck-table">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-1 pr-3">Invoice</th>
                      <th className="py-1 pr-3">Entity</th>
                      <th className="py-1 pr-3">Amount</th>
                      <th className="py-1 pr-3">Sync error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-muted/50">
                        <td className="py-1 pr-3">{inv.invoice_number}</td>
                        <td className="py-1 pr-3">{inv.entity_id}</td>
                        <td className="py-1 pr-3">{inv.amount} {inv.currency}</td>
                        <td className="py-1 pr-3 text-red-600 dark:text-red-400">{inv.accounting_sync_error || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {applied && (
          <div
            className={cn('text-xs', applied.applied ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400')}
            data-testid="queue-stuck-apply-result"
          >
            {applied.applied ? `Queued ${applied.queued_count} invoice(s).` : 'Apply failed.'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AfpvInvoiceComboMatchPanel() {
  const [txAmount, setTxAmount] = useState('');
  const [date, setDate] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!txAmount.trim() || !date.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.afpvInvoiceComboMatch(txAmount.trim(), date.trim());
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">AFPV Invoice Combo Match Census</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Report-only. Given a transaction amount and date, lists open AFPV invoices that
          exactly match, plus any 2-4 invoice combination summing to the amount within 0.01
          in a date window. Tests the batched-payout hypothesis. Never links or writes anything.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            data-testid="afpv-combo-match-amount-input"
            placeholder="tx_amount e.g. 2253.48"
            value={txAmount}
            onChange={(e) => setTxAmount(e.target.value)}
            className="max-w-[160px]"
          />
          <Input
            data-testid="afpv-combo-match-date-input"
            placeholder="YYYY-MM-DD"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            className="max-w-[160px]"
          />
          <Button data-testid="afpv-combo-match-run" onClick={run} disabled={loading || !txAmount.trim() || !date.trim()} size="sm">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Run
          </Button>
        </div>

        {result?.error && (
          <p className="text-xs text-red-600 dark:text-red-400">{result.error}</p>
        )}

        {result && !result.error && (
          <div className="border rounded-lg p-3 space-y-3 text-xs" data-testid="afpv-combo-match-result">
            <div className="text-muted-foreground">
              tx_amount={result.tx_amount}, window=[{result.date_window.start}, {result.date_window.end}],
              {' '}candidate pool={result.candidate_pool_size}
              {result.candidate_pool_truncated && ` (truncated to ${result.candidate_cap})`}
            </div>

            <div>
              <div className="font-medium mb-1">Exact single matches ({result.exact_single_matches.length})</div>
              {result.exact_single_matches.length === 0 && <div className="text-muted-foreground">none</div>}
              {result.exact_single_matches.map((inv) => (
                <div key={inv.id} className="pl-2 border-l-2 mb-1">
                  {inv.invoice_number} -- {inv.amount} {inv.currency}, issue_date={inv.issue_date}
                </div>
              ))}
            </div>

            <div>
              <div className="font-medium mb-1">Combination matches ({result.combo_match_count})</div>
              {result.combo_match_count === 0 && <div className="text-muted-foreground">none</div>}
              {result.combo_matches.map((c, i) => (
                <div key={i} className="pl-2 border-l-2 mb-1">
                  {c.invoice_count} invoices sum to {c.sum}: {c.invoices.map((inv) => inv.invoice_number).join(' + ')}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
